import type { TokenCredential } from "@azure/identity";
import {
  createCoderVersion,
  getAgentIdentityPrincipalId,
  getVersionStatus,
  type CoderMetadata,
} from "./foundry-agents.js";
import { grantFoundryUser } from "./rbac.js";
import { LocalCliError } from "./errors.js";
import type { FoundryProject } from "./foundry-project.js";

export interface DeployResult { version: string; principalId: string; status: string }

export async function deployHostedWorker(args: {
  credential: TokenCredential;
  subscriptionId: string;
  project: FoundryProject;
  name: string;
  image: string;
  cpu: string;
  memory: string;
  env: Record<string, string>;
  metadata: CoderMetadata;
  onProgress?: (msg: string) => void;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  pollTimeoutMs?: number;
  pollIntervalMs?: number;
}): Promise<DeployResult> {
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- noop progress stub
  const onProgress = args.onProgress ?? ((_m: string) => {});
  const sleep = args.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const now = args.now ?? (() => Date.now());
  const timeout = args.pollTimeoutMs ?? 8 * 60 * 1000;
  const interval = args.pollIntervalMs ?? 10_000;

  // 1. Create the version (SDK).
  onProgress(`creating version for '${args.name}'…`);
  const version = await createCoderVersion({
    credential: args.credential,
    endpoint: args.project.endpoint,
    name: args.name,
    image: args.image,
    cpu: args.cpu,
    memory: args.memory,
    env: args.env,
    metadata: args.metadata,
  });

  // 2. Read the per-version agent identity OID.
  const principalId = await getAgentIdentityPrincipalId({
    credential: args.credential,
    endpoint: args.project.endpoint,
    name: args.name,
    version,
  });

  // 3. Grant Foundry User to the agent identity — BEFORE polling. Skipping this
  //    is a load-bearing gotcha: the model call 401s and surfaces as a
  //    misleading storage_error. Idempotent (per-version identity).
  onProgress("granting Foundry User to the agent identity…");
  await grantFoundryUser({
    credential: args.credential,
    subscriptionId: args.subscriptionId,
    accountScope: args.project.accountScope,
    principalId,
  });

  // 4. Poll to active.
  const start = now();
  for (;;) {
    const status = await getVersionStatus({
      credential: args.credential,
      endpoint: args.project.endpoint,
      name: args.name,
      version,
    });
    onProgress(`status: ${status}`);
    if (status === "active") return { version, principalId, status };
    if (status === "failed") {
      throw new LocalCliError({
        code: "VERSION_FAILED",
        message: `Hosted agent '${args.name}' version ${version} entered status 'failed'.`,
        hint: "Inspect the container logs (App Insights exceptions / get_session_log_stream). storage_error with NO output_text.delta ⇒ a missing model-call RBAC grant; after full output ⇒ a platform persist issue.",
      });
    }
    if (now() - start > timeout) {
      throw new LocalCliError({
        code: "VERSION_POLL_TIMEOUT",
        message: `Hosted agent '${args.name}' version ${version} did not reach 'active' within ${Math.round(timeout / 1000).toString()}s (last status: ${status}).`,
        hint: "Provisioning can take 2–5 min; re-run with the same name to resume, or check the version status in the Foundry portal.",
      });
    }
    await sleep(interval);
  }
}
